// src/components/ProductosData/ProductosData.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash,
    faEdit,
    faArrowUp,
    faArrowDown,
    faSync,
    faEye,
    faBoxOpen,
    faFileExcel,
    faFilePdf,
    faSpinner,
    faImages,
    faDollarSign,
    faToggleOn,
    faToggleOff,
    faEyeSlash,
    faPalette,
    faRuler
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Link as Anchor } from 'react-router-dom';

import './ProductosData.css';
import EditProduct from '../EditProduct/EditProduct';
import baseURL from '../../url';
import moneda from '../../moneda';
import NewProduct from '../NewProduct/NewProduct';

// Componente para manejar los toasts
const CustomToast = ({ toasts }) => {
    if (toasts.length === 0) return null;
    return (
        <div className="product-toast-container">
            {toasts.map(t => (
                <div key={t.id} className={`product-toast ${t.type}`}>
                    <div className="toast-icon">
                        {t.type === 'success' && '✅'}
                        {t.type === 'error' && '❌'}
                        {t.type === 'warning' && '⚠️'}
                        {t.type === 'info' && 'ℹ️'}
                    </div>
                    <div className="toast-content">
                        <div className="toast-title">{t.message}</div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default function ProductosData() {
    // Data
    const [productos, setProductos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(false);

    // UI
    const [ordenInvertido, setOrdenInvertido] = useState(false);
    const [toasts, setToasts] = useState([]);

    // Filtros
    const [filtroId, setFiltroId] = useState('');
    const [filtroTitulo, setFiltroTitulo] = useState('');
    const [filtroCategoria, setFiltroCategoria] = useState('');
    const [filtroMasVendido, setFiltroMasVendido] = useState('');

    // Modal edición
    const [modalVisible, setModalVisible] = useState(false);
    const [producto, setProducto] = useState({});

    // Toast helper
    const showToast = (message, type = 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
    };

    // Loaders
    const cargarProductos = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${baseURL}/productosGet.php`);
            const data = await res.json();
            setProductos(data.productos || []);
        } catch (e) {
            console.error(e);
            showToast('Error al cargar productos', 'error');
        } finally {
            setLoading(false);
        }
    };

    const cargarCategoria = async () => {
        try {
            const res = await fetch(`${baseURL}/categoriasGet.php`);
            const data = await res.json();
            setCategorias(data.categorias || []);
        } catch (e) {
            console.error(e);
            showToast('Error al cargar categorías', 'error');
        }
    };

    useEffect(() => {
        cargarProductos();
        cargarCategoria();
    }, []);

    // Filtros
    const productosFiltrados = useMemo(() => {
        const list = [...productos];
        const filtered = list.filter(item => {
            const idMatch = filtroId === '' || (item.idProducto + '').includes(filtroId);
            const tituloMatch = filtroTitulo === '' || (item.titulo || '').toLowerCase().includes(filtroTitulo.toLowerCase());
            const categoriaMatch = filtroCategoria === '' || (item.idCategoria + '') === (filtroCategoria + '');
            const masVendidoMatch = filtroMasVendido === '' || (item.masVendido || '').toLowerCase() === filtroMasVendido.toLowerCase();
            return idMatch && tituloMatch && categoriaMatch && masVendidoMatch;
        });
        return ordenInvertido ? filtered.reverse() : filtered;
    }, [productos, filtroId, filtroTitulo, filtroCategoria, filtroMasVendido, ordenInvertido]);

    const refreshData = () => {
        cargarProductos();
        showToast('Productos actualizados', 'info');
    };

    // Acciones
    const toggleProductoEstado = (idProducto, titulo, estadoActual) => {
        const accion = estadoActual === 1 ? 'deshabilitar' : 'habilitar';
        const estadoTexto = estadoActual === 1 ? 'deshabilitado' : 'habilitado';
        
        Swal.fire({
            title: `¿${accion.charAt(0).toUpperCase() + accion.slice(1)} producto?`,
            text: `El producto "${titulo}" será ${estadoTexto}`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: estadoActual === 1 ? '#ef4444' : '#10b981',
            cancelButtonColor: '#6b7280',
            confirmButtonText: `Sí, ${accion}`,
            cancelButtonText: 'Cancelar',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await fetch(`${baseURL}/productToggleStatus.php?idProducto=${idProducto}`, { method: 'PUT' });
                    
                    if (!res.ok) {
                        console.error(`HTTP Error: ${res.status} ${res.statusText}`);
                        showToast(`Error HTTP ${res.status}: ${res.statusText}`, 'error');
                        return;
                    }
                    
                    const data = await res.json();
                    console.log('Toggle response:', data);
                    
                    if (data.mensaje) {
                        showToast(data.mensaje, 'success');
                        cargarProductos();
                    } else {
                        const errorMsg = data.error || 'No se pudo cambiar el estado';
                        const details = data.details ? ` - ${data.details}` : '';
                        showToast(errorMsg + details, 'error');
                        console.error('Backend error:', data);
                    }
                } catch (e) {
                    console.error('Fetch error:', e);
                    showToast(`Error de conexión: ${e.message}`, 'error');
                }
            }
        });
    };

    const abrirModal = (item) => {
        setProducto(item);
        setModalVisible(true);
    };

    const cerrarModal = () => {
        setModalVisible(false);
        setProducto({});
    };

    // Exports
    const descargarExcel = () => {
        const data = productosFiltrados.map(item => ({
            IdProducto: item.idProducto,
            Titulo: item.titulo,
            Descripcion: item.descripcion,
            Precio: item.precio,
            PrecioAnterior: item.precioAnterior,
            StockVariaciones: item.stockVariaciones ?? '',
            StockProducto: item.stock ?? '',
            Categoria: item.categoriaNombre ?? '',
            Fecha: item.createdAt,
            Imagen: item.imagenPrincipal || item.imagen1 || '',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Productos');
        XLSX.writeFile(wb, 'productos.xlsx');
    };

    const descargarPDF = () => {
        const pdf = new jsPDF();
        pdf.text('Lista de Productos', 14, 16);

        const rows = productosFiltrados.map(p => ({
            id: p.idProducto,
            titulo: p.titulo,
            precio: `${moneda} ${(`${p?.precio ?? 0}`).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`,
            stockVar: p.stockVariaciones ?? p.stock ?? 0,
            fecha: p.createdAt?.substring(0, 10) ?? '',
        }));

        pdf.autoTable({
            head: [['ID', 'Título', 'Precio', 'Stock Var.', 'Fecha']],
            body: rows.map(r => [r.id, r.titulo, r.precio, r.stockVar, r.fecha]),
            startY: 22,
            styles: { fontSize: 9 },
            headStyles: { fillColor: [56, 50, 124] },
        });

        pdf.save('productos.pdf');
    };

    // Helpers
    const nombreCategoria = (idCat) => categorias.find(c => c.idCategoria === idCat)?.categoria ?? '—';

    return (
        <div>
            <CustomToast toasts={toasts} />

            {/* Header */}
            <div className="global-component-header" style={{ padding: '12px 16px' }}>
                <div className="flex-between">
                    <div className="flex-center" style={{ gap: '16px' }}>
                        <div className="global-component-title">
                            <FontAwesomeIcon icon={faBoxOpen} />
                            Gestión de Productos
                        </div>
                        <div className="flex-center" style={{ gap: '12px' }}>
                            <div className="global-stat-card compact" >
                                <span className="global-stat-number">{productos.length}</span>
                                <span className="global-stat-label">Productos</span>
                            </div>
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">
                                    {productos.reduce((acc, p) => acc + (Number(p.stockVariaciones ?? 0)), 0)}
                                </span>
                                <span className="global-stat-label">Stock Total</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex-center" style={{ gap: '8px' }}>
                        <button className="btn-product-primary compact flex-center" style={{ marginLeft: "10px" }} onClick={descargarExcel} title="Exportar Excel">
                            <FontAwesomeIcon icon={faFileExcel} />
                        </button>
                        <button className="btn-product-primary compact flex-center" onClick={descargarPDF} title="Exportar PDF">
                            <FontAwesomeIcon icon={faFilePdf} />
                        </button>
                        <button className="btn-product-secondary compact flex-center" onClick={refreshData} title="Actualizar">
                            <FontAwesomeIcon icon={faSync} />
                        </button>
                    </div>
                </div>
            </div>
            <div className="new-product-container">
                <NewProduct />
            </div>

            {/* Filtros */}
            <div className="product-section-card compact" style={{ margin: '8px 0' }}>
                <div className="product-section-content" style={{ padding: '12px 16px' }}>
                    <div className="flex-between" style={{ gap: '12px', alignItems: 'end' }}>
                        <div className="flex-center" style={{ gap: '12px', flex: 1 }}>
                            <div className="product-form-group compact">
                                <label className="product-form-label">Título</label>
                                <input
                                    className="product-form-input compact"
                                    value={filtroTitulo}
                                    onChange={(e) => setFiltroTitulo(e.target.value)}
                                    placeholder="Buscar..."
                                />
                            </div>
                            <div className="product-form-group compact">
                                <label className="product-form-label">
                                    Categoría
                                </label>
                                <select
                                    className="product-form-input compact"
                                    value={filtroCategoria}
                                    onChange={(e) => setFiltroCategoria(e.target.value)}
                                >
                                    <option value="">Todas</option>
                                    {categorias.map(cat => (
                                        <option key={cat.idCategoria} value={cat.idCategoria}>
                                            {cat.categoria}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="product-form-group compact">
                                <label className="product-form-label">Más vendido</label>
                                <select
                                    className="product-form-input compact"
                                    value={filtroMasVendido}
                                    onChange={(e) => setFiltroMasVendido(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    <option value="si">Sí</option>
                                    <option value="no">No</option>
                                </select>
                            </div>
                        </div>

                        <button
                            className="btn-product-secondary compact flex-center"
                            onClick={() => setOrdenInvertido(p => !p)}
                            title="Invertir orden"
                            style={{ minWidth: '80px' }}
                        >
                            {ordenInvertido ? <FontAwesomeIcon icon={faArrowUp} /> : <FontAwesomeIcon icon={faArrowDown} />}
                            <span style={{ marginLeft: '4px' }}>Orden</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Contenedor principal de la tabla/tarjetas */}
            <div className="global-table-container">
                {loading ? (
                    <div className="global-loading-state">
                        <FontAwesomeIcon icon={faSpinner} size="2x" />
                        <div>Cargando productos...</div>
                    </div>
                ) : productosFiltrados.length > 0 ? (
                    <>
                        {/* Vista para Desktop: Tabla */}
                        <table className="global-table desktop-only">
                            <thead>
                                <tr>
                                    <th>Título</th>
                                    <th><FontAwesomeIcon icon={faDollarSign} style={{ marginRight: 8 }} />Precio</th>
                                    <th>Categoría</th>
                                    <th><FontAwesomeIcon icon={faImages} style={{ marginRight: 8 }} />Imagen</th>
                                    <th>Stock (Var.)</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productosFiltrados.map((item, idx) => (
                                    <tr key={item.idProducto} style={{ animationDelay: `${idx * 0.05}s` }}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ 
                                                    width: 8, 
                                                    height: 8, 
                                                    borderRadius: '50%', 
                                                    backgroundColor: item.activo === 1 ? '#10b981' : '#ef4444' 
                                                }}></span>
                                                <div>
                                                    <div>{item.titulo}</div>
                                                    {/* Variaciones compactas */}
                                                    {item.variaciones && (item.variaciones.total_colores > 0 || item.variaciones.total_tallas > 0) && (
                                                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                                                            {item.variaciones.total_colores > 0 && (
                                                                <span style={{ marginRight: 8 }}>
                                                                    🎨 {item.variaciones.total_colores} color{item.variaciones.total_colores !== 1 ? 'es' : ''}
                                                                </span>
                                                            )}
                                                            {item.variaciones.total_tallas > 0 && (
                                                                <span>
                                                                    📏 {item.variaciones.total_tallas} talla{item.variaciones.total_tallas !== 1 ? 's' : ''}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                {item.activo === 0 && (
                                                    <span style={{ 
                                                        fontSize: 11, 
                                                        background: '#ef4444', 
                                                        color: 'white', 
                                                        padding: '2px 6px', 
                                                        borderRadius: 4 
                                                    }}>
                                                        INACTIVO
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ color: '#008000', fontWeight: 600 }}>
                                            {moneda} {`${item?.precio ?? 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                                        </td>
                                        <td style={{ color: 'var(--color2)', fontWeight: 600 }}>
                                            {item.categoriaNombre ?? nombreCategoria(item.idCategoria)}
                                        </td>
                                        <td>
                                            {item.imagenPrincipal ? (
                                                <img
                                                    src={item.imagenPrincipal}
                                                    alt="principal"
                                                    style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--gris2)' }}
                                                />
                                            ) : (
                                                <span className="text-muted">Sin imagen</span>
                                            )}
                                        </td>
                                        <td>{item.stockVariaciones ?? item.stock ?? 0}</td>
                                        <td>
                                            <div className="global-table-actions">
                                                <button className="global-btn-edit" onClick={() => abrirModal(item)} title="Editar">
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                <button
                                                    className={item.activo === 1 ? "global-btn-delete" : "global-btn-edit"}
                                                    onClick={() => toggleProductoEstado(item.idProducto, item.titulo, item.activo)}
                                                    title={item.activo === 1 ? "Deshabilitar producto" : "Habilitar producto"}
                                                >
                                                    <FontAwesomeIcon icon={item.activo === 1 ? faEyeSlash : faEye} />
                                                </button>
                                                <Anchor
                                                    className="global-btn-manage"
                                                    to={`/producto/${item?.idProducto}/${(item?.titulo || '').replace(/\s+/g, '-')}`}
                                                    title="Ver"
                                                >
                                                    <FontAwesomeIcon icon={faEye} />
                                                </Anchor>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Vista para Móvil: Tarjetas */}
                        <div className="card-list mobile-only">
                            {productosFiltrados.map((item, idx) => (
                                <div key={item.idProducto} className="product-card" style={{ animationDelay: `${idx * 0.05}s` }}>
                                    <div className="card-header">
                                        <div className="card-image-container">
                                            {item.imagenPrincipal ? (
                                                <img src={item.imagenPrincipal} alt={item.titulo} className="card-image" />
                                            ) : (
                                                <div className="card-image-placeholder">
                                                    <FontAwesomeIcon icon={faImages} size="2x" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="card-info">
                                            <h4 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ 
                                                    width: 6, 
                                                    height: 6, 
                                                    borderRadius: '50%', 
                                                    backgroundColor: item.activo === 1 ? '#10b981' : '#ef4444' 
                                                }}></span>
                                                {item.titulo}
                                                {item.activo === 0 && (
                                                    <span style={{ 
                                                        fontSize: 10, 
                                                        background: '#ef4444', 
                                                        color: 'white', 
                                                        padding: '2px 4px', 
                                                        borderRadius: 3 
                                                    }}>
                                                        INACTIVO
                                                    </span>
                                                )}
                                            </h4>
                                            <span className="card-category">
                                                {item.categoriaNombre ?? nombreCategoria(item.idCategoria)}
                                            </span>
                                            
                                            {/* Información de variaciones */}
                                            {item.variaciones && (
                                                <div style={{ margin: '8px 0', fontSize: 12 }}>
                                                    {/* Colores */}
                                                    {item.variaciones.total_colores > 0 && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                                            <FontAwesomeIcon icon={faPalette} style={{ color: '#6b7280', fontSize: 10 }} />
                                                            <span style={{ color: '#6b7280' }}>
                                                                {item.variaciones.total_colores} color{item.variaciones.total_colores !== 1 ? 'es' : ''}:
                                                            </span>
                                                            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                                                {item.variaciones.colores.slice(0, 4).map((color, idx) => (
                                                                    <div
                                                                        key={idx}
                                                                        style={{
                                                                            width: 12,
                                                                            height: 12,
                                                                            borderRadius: '50%',
                                                                            backgroundColor: color.hex,
                                                                            border: '1px solid #d1d5db',
                                                                            flexShrink: 0
                                                                        }}
                                                                        title={color.nombre}
                                                                    />
                                                                ))}
                                                                {item.variaciones.total_colores > 4 && (
                                                                    <span style={{ fontSize: 10, color: '#6b7280' }}>
                                                                        +{item.variaciones.total_colores - 4}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Tallas */}
                                                    {item.variaciones.total_tallas > 0 && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <FontAwesomeIcon icon={faRuler} style={{ color: '#6b7280', fontSize: 10 }} />
                                                            <span style={{ color: '#6b7280' }}>
                                                                {item.variaciones.total_tallas} talla{item.variaciones.total_tallas !== 1 ? 's' : ''}:
                                                            </span>
                                                            <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                                {item.variaciones.tallas_disponibles.slice(0, 6).map((talla, idx) => (
                                                                    <span
                                                                        key={idx}
                                                                        style={{
                                                                            fontSize: 9,
                                                                            background: '#f3f4f6',
                                                                            color: '#374151',
                                                                            padding: '1px 4px',
                                                                            borderRadius: 3,
                                                                            border: '1px solid #e5e7eb'
                                                                        }}
                                                                    >
                                                                        {talla}
                                                                    </span>
                                                                ))}
                                                                {item.variaciones.total_tallas > 6 && (
                                                                    <span style={{ fontSize: 9, color: '#6b7280' }}>
                                                                        +{item.variaciones.total_tallas - 6}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <span className="card-price">
                                                {moneda} {`${item?.precio ?? 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="card-footer">
                                        <div className="card-stock">
                                            <span className="stock-label">Stock:</span>
                                            <span className="stock-value">{item.stockVariaciones ?? item.stock ?? 0}</span>
                                        </div>
                                        <div className="card-actions">
                                            <button className="global-btn-edit" onClick={() => abrirModal(item)} title="Editar">
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button
                                                className={item.activo === 1 ? "global-btn-delete" : "global-btn-edit"}
                                                onClick={() => toggleProductoEstado(item.idProducto, item.titulo, item.activo)}
                                                title={item.activo === 1 ? "Deshabilitar producto" : "Habilitar producto"}
                                            >
                                                <FontAwesomeIcon icon={item.activo === 1 ? faEyeSlash : faEye} />
                                            </button>
                                            <Anchor
                                                className="global-btn-manage"
                                                to={`/producto/${item?.idProducto}/${(item?.titulo || '').replace(/\s+/g, '-')}`}
                                                title="Ver"
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                            </Anchor>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="global-empty-state">
                        <FontAwesomeIcon icon={faBoxOpen} size="3x" />
                        <div className="global-empty-title">No hay productos</div>
                        <div className="global-empty-description">
                            Crea un producto con el botón “Nuevo Producto” y empezará a aparecer aquí.
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de edición */}
            {modalVisible && producto?.idProducto && (
                <EditProduct
                    product={producto}
                    onClose={cerrarModal}
                    onSaved={cargarProductos}
                    categorias={categorias}
                    showToast={showToast}
                    moneda={moneda}
                />
            )}
        </div>
    );
}